import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import {
    CellBadge,
    CellDatetime,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { CellLinkWithIcon } from 'src/registry/components/table/cells/CellLinkWithIcon';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import useFilters from 'src/registry/hooks/useFilters';
import usePagination from 'src/registry/hooks/usePagination';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';
import { commonMessages, generalMessages } from 'src/utils/messages';
import { BillingRun } from 'src/features/billingRuns/utils/types';
import { useListBillingRuns } from 'src/features/billingRuns/api/useListBillingRuns';
import { billingRunsMessages } from 'src/features/billingRuns/utils/messages';
import {
    BILLING_RUN_STATUS_BADGE_OPTIONS,
    BILLING_RUN_TYPE_BADGE_OPTIONS,
} from 'src/features/billingRuns/utils/constants';
import CellBillingRunActions from 'src/features/billingRuns/components/tables/CellBillingRunActions';
import TriggerBillingRunFormModal from 'src/features/billingRuns/components/forms/TriggerBillingRunFormModal';

const BillingRunsTable = () => {
    const { formatMessage } = useIntl();

    const permissionManageCharges = useHasPermissions([PERMISSION.MANAGE_CHARGES]);

    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'business_date' });
    const { queryFiltersOptions, ...filters } = useFilters();
    // Newest first is the right default for an ops log.
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'business_date', desc: true }],
    });
    const { queryPaginationOptions, ...pagination } = usePagination();

    const {
        data: billingRuns,
        isError,
        isLoading,
    } = useListBillingRuns({
        ...querySearchOptions,
        ...queryFiltersOptions,
        ...querySortingOptions,
        ...queryPaginationOptions,
    });

    const columnHelper = createColumnHelper<BillingRun>();

    const columns = [
        // A run has no name, so the date is what identifies the row and carries the link.
        columnHelper.accessor('business_date', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableColumnFilter: true,
            cell: (ctx) => (
                <CellLinkWithIcon
                    {...ctx}
                    to={`/billing-runs/${ctx.row.original.id}`}
                    render={(businessDate) => <div className="text-sm font-medium">{businessDate}</div>}
                />
            ),
            meta: {
                title: formatMessage(billingRunsMessages.businessDate),
                inputType: INPUT_TYPE.DATE,
            },
        }),
        columnHelper.accessor('type', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(commonMessages.type),
                badgeOptions: BILLING_RUN_TYPE_BADGE_OPTIONS,
                inputType: INPUT_TYPE.MULTI_SELECT,
            },
        }),
        columnHelper.accessor('status', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(generalMessages.status),
                badgeOptions: BILLING_RUN_STATUS_BADGE_OPTIONS,
                inputType: INPUT_TYPE.MULTI_SELECT,
            },
        }),
        columnHelper.accessor('triggered_by', {
            header: DataTableColumnHeader,
            enableHiding: true,
            // Null means the scheduled run, not missing data.
            cell: ({ row }) => row.original.triggered_by?.name ?? formatMessage(billingRunsMessages.scheduledRun),
            meta: {
                title: formatMessage(billingRunsMessages.triggeredBy),
            },
        }),
        columnHelper.accessor('total_count', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: { title: formatMessage(billingRunsMessages.totalCount) },
        }),
        columnHelper.accessor('succeeded_count', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: { title: formatMessage(billingRunsMessages.succeededCount) },
        }),
        columnHelper.accessor('failed_count', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: { title: formatMessage(billingRunsMessages.failedCount) },
        }),
        columnHelper.accessor('skipped_count', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: { title: formatMessage(billingRunsMessages.skippedCount) },
        }),
        columnHelper.accessor('started_at', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: CellDatetime,
            meta: { title: formatMessage(billingRunsMessages.startedAt) },
        }),
        columnHelper.accessor('finished_at', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: CellDatetime,
            meta: { title: formatMessage(billingRunsMessages.finishedAt) },
        }),
        columnHelper.display({
            id: 'actions',
            header: DataTableColumnHeader,
            cell: CellBillingRunActions,
            maxSize: 45,
        }),
    ];

    return (
        <DataTable<BillingRun, unknown>
            columns={columns}
            data={billingRuns}
            semantics={{
                title: formatMessage(billingRunsMessages.billingRuns),
                entryName: formatMessage(billingRunsMessages.billingRun).toLowerCase(),
                entryNames: formatMessage(billingRunsMessages.billingRuns).toLowerCase(),
                placeholder: formatMessage(billingRunsMessages.billingRun).toLowerCase(),
            }}
            search={search}
            filters={filters}
            sorting={sorting}
            pagination={pagination}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(permissionManageCharges && { newEntry: TriggerBillingRunFormModal }),
            }}
        />
    );
};

export default BillingRunsTable;
